/** Message type identifiers exchanged over the Colyseus room. */
export const MessageType = {
    Move: "move",
    /** Client → server: intent to pick up the nearest in-range ground item (no body). */
    Pickup: "pickup",
    /** Client → server: ask the server to (re)send the owner's inventory (no body). */
    RequestInventory: "reqInv",
    /** Server → owning client: the authoritative inventory contents. */
    Inventory: "inventory",
    /** Server → owning client: a pickup could not be completed. */
    PickupRejected: "pickupRejected",
    /** Client → server: drop one unit of an inventory slot onto the ground ({ slotIndex }). */
    Drop: "drop",
    /** Client → server: equip the item in an inventory slot ({ slotIndex }). */
    Equip: "equip",
    /** Client → server: unequip the garment in a slot back to the bag ({ slot }; gear, hand, neck, or cosmetic). */
    Unequip: "unequip",
    /** Client → server: toggle whether the player's cosmetics are shown ({ show }). */
    ShowCosmetics: "showCosmetics",
    /** Client → server: consume one unit of a usable item in a slot ({ slotIndex }). */
    UseItem: "useItem",
    /** Client → server: use the item worn in the NECK slot (e.g. summon/dismiss the pet from a worn Hog Charm). */
    UseNeck: "useNeck",
    /** Client → server: drag-and-drop reorder — move/swap a bag slot ({ from, to }). */
    MoveItem: "moveItem",
    /** Client → server: take one item out of a nearby loot chest ({ chestId, index }). */
    TakeChestItem: "takeChestItem",
    /** Client → server: a mining channel completed on a node ({ nodeId }). */
    Mine: "mine",
    /** Client → server: a skinning channel completed on a dead mob corpse ({ hogId }). */
    Skin: "skin",
    /** Client → server: ask the skinner NPC to teach a profession ({ profession }). */
    LearnProfession: "learnProfession",
    /** Client → server: a single crafting unit's channel completed at a station ({ recipeId, quantity }). */
    Craft: "craft",
    /** Server → owning client: the result of one craft unit (succeeded/attempted counts). */
    CraftResult: "craftResult",
    /** Client → server: started a crafting channel — sets the synced `crafting` flag so other players
     *  see the spinning-gear animation (no payload). */
    CraftStart: "craftStart",
    /** Client → server: a crafting channel ended (completed or cancelled) — clears `crafting` (no payload). */
    CraftStop: "craftStop",
    /** Client → server: started a mining channel — sets the synced `mining` flag so other players
     *  see the swinging-pickaxe animation (no payload). */
    MineStart: "mineStart",
    /** Client → server: a mining channel ended (completed or cancelled) — clears `mining` (no payload). */
    MineStop: "mineStop",
    /** Client → server: interact with the miner NPC ({ kind: "pickaxe" | "sell" }). */
    MinerAction: "minerAction",
    /** Client → server: a fishing catch succeeded ({ kind } — which fish was landed). */
    Fish: "fish",
    /** Client → server: started fishing (line cast) — sets the synced `fishing` flag (no payload). */
    FishStart: "fishStart",
    /** Client → server: stopped fishing ({ escaped? } — an escape still eats the bait). */
    FishStop: "fishStop",
    /** Client → server: the fishing ROD stance changed ({ state: 0-4 }) — synced to Player.fishState. */
    FishState: "fishState",
    /** Client → server (DEBUG/admin, dev builds): set my fishing level ({ level }). */
    FishLevelSet: "fishLevelSet",
    /** Client → server: cut a grass tuft for cash ({ nodeId }). */
    CutGrass: "cutGrass",
    /** Client → server: claim the grass-cutter from the Gardener (no body). */
    GardenerAction: "gardenerAction",
    /** Server → owning client: a skill levelled up (drives the bottom notification). */
    SkillLevelUp: "skillLevelUp",
    /** Server → owning client: a transient flavour notice (toast), e.g. "Sold ore for $X". */
    Notice: "notice",
    /** Client → server: ATM move cash → bank ({ amount }). */
    Deposit: "deposit",
    /** Client → server: ATM move bank → cash ({ amount }). */
    Withdraw: "withdraw",
    /** Client → server: buy a clothing-shop item with cash ({ itemId }). */
    Buy: "buy",
    /** Client → server: pay the salon to change hair style + colour ({ hair, hairColor }). */
    SetHair: "setHair",
    /** Client → server: open a shared timed portal ({ kind, zone, x, y }). */
    OpenPortal: "openPortal",
    /** Client → server: the city Winston teleported the player home — grant one return scroll. */
    WinstonTeleport: "winstonTeleport",
    /** Server → owning client: an equip/unequip/drop could not be completed. */
    EquipRejected: "equipRejected",
    /** Server → owning client: the authoritative wallet balance (private to the owner). */
    Balance: "balance",
    /** Client → server: intent to sit at the blackjack table (no body). */
    JoinTable: "sit",
    /** Client → server: intent to leave the table — only honored between rounds (no body). */
    LeaveTable: "leaveTable",
    /** Server → owning client: which seat index the client now holds (-1 = none). */
    TableSeat: "tableSeat",
    /** Server → owning client: a sit/leave intent could not be completed. */
    TableRejected: "tableRejected",
    /** Client → server: place/replace a bet during the betting window ({ amount }). */
    PlaceBet: "placeBet",
    /** Client → server: hit the active hand (no body). */
    Hit: "hit",
    /** Client → server: stand the active hand (no body). */
    Stand: "stand",
    /** Client → server: double down — match the wager, take one card, stand (no body). */
    Double: "double",
    /** Client → server: split a two-equal-rank hand into two (no body). */
    Split: "split",
    /** Server → owning client: a bet/turn action was rejected. */
    ActionRejected: "actionRejected",
    // ── Texas Hold'em poker (separate table from blackjack) ──────────────────────────────
    /** Client → server: intent to sit at the poker table (no body). */
    JoinPokerTable: "pokerSit",
    /** Client → server: cash out the chip stack — paid after the current hand settles (no body). */
    PokerWithdraw: "pokerWithdraw",
    /** Client → server: leave the poker table — cashes out after the current hand (no body). */
    LeavePokerTable: "pokerLeave",
    /** Client → server: buy in chips from the cash wallet at the poker table ({ amount }). */
    PokerBuyIn: "pokerBuyIn",
    /** Client → server: a poker turn action ({ action: "fold"|"check"|"call"|"raise", amount? }). */
    PokerAct: "pokerAct",
    /** Server → owning client: which poker seat index the client now holds (-1 = none). */
    PokerSeated: "pokerSeated",
    /** Server → owning client: your PRIVATE hole cards for the current hand ({ cards }). */
    PokerHole: "pokerHole",
    /** Server → owning client: a sit/leave/buy-in intent could not be completed ({ reason }). */
    PokerRejected: "pokerRejected",
    /** Server → owning client: a poker turn action was rejected ({ reason }). */
    PokerActionRejected: "pokerActRej",
    /** Client → server: a building owner/admin toggles its lock ({ buildingId }). */
    ToggleLock: "toggleLock",
    /** Client → server: a player applies for a job at a building ({ buildingId }). */
    ApplyJob: "applyJob",
    /** Client → server: owner approves/rejects a pending applicant ({ buildingId, characterId, accept }). */
    ReviewApplication: "reviewApp",
    /** Client → server: owner fires a staff member ({ buildingId, characterId }). */
    Fire: "fire",
    /** Client → server: buy a building that's listed for sale, from its owner ({ buildingId }). */
    BuyBuilding: "buyBuilding",
    /** Client → server: list / unlist your building for sale at a custom price ({ buildingId, price }). */
    ListBuilding: "listBuilding",
    /** Client → server: a house owner gives a door key to an online player ({ buildingId, characterName }). */
    GiveKey: "giveKey",
    /** Client → server: a house owner revokes a door key ({ buildingId, characterId }). */
    RevokeKey: "revokeKey",
    /** Client → server: toggle a house's window curtains ({ buildingId }). */
    ToggleCurtains: "toggleCurtains",
    /** Client → server: admin starts a building auction ({ buildingId, durationHours, startPrice, minIncrement }). */
    StartAuction: "startAuction",
    /** Client → server: bid on a live auction ({ buildingId, amount }) — held from the bidder's bank. */
    BidAuction: "bidAuction",
    /** Client → server: admin cancels a live auction ({ buildingId }) — top bidder refunded. */
    CancelAuction: "cancelAuction",
    /** Server → owning client: the character's quest log ({ quests }) — on join + after progress. */
    QuestLog: "questLog",
    /** Client → server: accept a quest from its giver NPC ({ questId }). */
    AcceptQuest: "acceptQuest",
    /** Client → server: turn in a submit-item quest at its giver ({ questId }). */
    SubmitQuestItems: "submitQuestItems",
    /** Client → server: load/unload a rod's bait slot ({ rod, worm?, action }). */
    RodBait: "rodBait",
    /** Client → server: mount/dismount the parked BMX ({ on }). */
    BmxMount: "bmxMount",
    /** Client → server: imprint a bagged fish onto the fishing records ({ slotIndex }). */
    ImprintFish: "imprintFish",
    /** Server → client: a fish was landed ({ kind, size }) — the catch popup. */
    FishCaught: "fishCaught",
    /** Client → server: ask for the fishing leaderboard (no body). */
    FishBoardRequest: "fishBoardRequest",
    /** Server → client: the fishing leaderboard snapshot. */
    FishBoard: "fishBoard",
    /** Client → server: a restaurant employee clocks in / out of their shift ({ buildingId } — toggles). */
    ClockToggle: "clockToggle",
    /** Client → server: open a building's shared staff fridge ({ buildingId }). Staff-only. */
    RequestFridge: "reqFridge",
    /** Server → staff client: a building fridge snapshot ({ buildingId, slots, capacity }). */
    Fridge: "fridge",
    /** Client → server: stock one unit from the bag into a building fridge ({ buildingId, slotIndex }). */
    FridgeStore: "fridgeStore",
    /** Client → server: take one unit from a building fridge back to the bag ({ buildingId, slotIndex }). */
    FridgeTake: "fridgeTake",
    /** Client → server: a completed cooking-pot channel unit ({ recipeId, quantity }). Watabaga-staff gated. */
    Cook: "cook",
    /** Client → server: a completed sewing-machine channel unit ({ recipeId }). Threads-staff gated. */
    Sew: "sew",
    /** Client → server: open the Watabaga NPC menu — ask for the building's offered items + live stock
     *  ({ buildingId }). */
    RequestFoodMenu: "reqFoodMenu",
    /** Server → client: the building's food menu (offered items + price + fridge stock). */
    FoodMenu: "foodMenu",
    /** Client → server: buy one stocked menu item from the NPC ({ buildingId, itemId }); pays the owner. */
    BuyFood: "buyFood",
    /** Client → server: owner sets which menu items the NPC offers ({ buildingId, items }). */
    SetMenu: "setMenu",
    /** Client → server: a finished drink-dispenser pour ({ buildingId, itemId }). Watabaga-staff gated. */
    Dispense: "dispense",
    /** Client → server: owner withdraws from the building register ({ buildingId, amount }). */
    CollectProfit: "collectProfit",
    /** Client → server: owner deposits cash into the building register ({ buildingId, amount }). */
    DepositFunds: "depositFunds",
    /** Client → server: owner sets a menu item's NPC price ({ buildingId, itemId, price }). */
    SetPrice: "setPrice",
    /** Client → server: owner sets a menu item's per-task wage cost ({ buildingId, itemId, cost }). */
    SetCost: "setCost",
    /** Client → server: owner caps how many of an item the store chest holds ({ buildingId, itemId, max }). */
    SetMax: "setMax",
    /** Client → server: place a furniture item from the bag inside a building ({ buildingId, slotIndex, x, y, z }). */
    PlaceFurniture: "placeFurni",
    /** Client → server: move a placed furniture piece ({ buildingId, fid, x, y, z }). */
    MoveFurniture: "moveFurni",
    /** Client → server: pick a placed furniture piece back up into the bag ({ buildingId, fid }). */
    PickupFurniture: "pickupFurni",
    /** Client → server: use a placed piece's function ({ buildingId, fid }) — e.g. sleep in a bed. */
    UseFurniture: "useFurni",
    /** Client → server: open a placed safebox — ask for its contents ({ buildingId, fid }). */
    RequestSafebox: "reqSafebox",
    /** Server → authorized client: a safebox snapshot (slots + capacity). */
    Safebox: "safebox",
    /** Client → server: move one unit from a bag slot INTO a safebox ({ buildingId, fid, slotIndex }). */
    SafeboxStore: "safeboxStore",
    /** Client → server: take one unit from a safebox slot back to the bag ({ buildingId, fid, slotIndex }). */
    SafeboxTake: "safeboxTake",
    /** Client → server: drag-reorder INSIDE a safebox ({ buildingId, fid, from, to }). */
    SafeboxReorder: "safeboxReorder",
    /** Server → owning client: base stats + bank money (private). */
    PlayerStats: "playerStats",
    /** Client → server: ask for the wealth rankboard (no body). */
    RequestLeaderboard: "reqLeaderboard",
    /** Server → owning client: the top-N wealth rankboard. */
    Leaderboard: "leaderboard",
    /** Client → server: toggle my rankboard anonymity ({ anonymous }). */
    SetAnonymous: "setAnonymous",
    /** Client → server: ask for my bank storage contents (no body). */
    RequestStorage: "reqStorage",
    /** Server → owning client: my bank storage (slots + capacity + next upgrade cost). */
    Storage: "storage",
    /** Client → server: move one unit from an inventory slot INTO storage ({ slotIndex }). */
    StoreItem: "storeItem",
    /** Client → server: move one unit from a storage slot back to the bag ({ slotIndex }). */
    RetrieveItem: "retrieveItem",
    /** Client → server: drag-reorder INSIDE the vault — move/merge/swap two vault slots ({ from, to }). */
    StorageReorder: "storageReorder",
    /** Client → server: buy the next storage capacity tier (no body). */
    UpgradeStorage: "upgradeStorage",
    /** Client → server: sell bag items to the vendor NPC for cash ({ slots: number[] }). */
    SellToNpc: "sellToNpc",
    /** Client → server: ask for the marketplace listings (no body). */
    RequestMarket: "reqMarket",
    /** Server → owning client: the marketplace snapshot ({ listings, fee/limit info }). */
    Market: "market",
    /** Client → server: list a bag item on the marketplace ({ slotIndex, quantity, price }). */
    MarketList: "marketList",
    /** Client → server: buy a listing, paying from bank ({ listingId }). */
    MarketBuy: "marketBuy",
    /** Client → server: cancel your own listing, returning the item ({ listingId }). */
    MarketCancel: "marketCancel",
    /** Client → server: ask a selected player to trade ({ to: sessionId }). */
    TradeRequest: "tradeReq",
    /** Server → target: someone wants to trade ({ from, fromName }). */
    TradeInvite: "tradeInvite",
    /** Client → server: accept / decline a pending trade invite ({ accept }). */
    TradeRespond: "tradeRespond",
    /** Client → server: set MY side of the open trade ({ items, cash }). */
    TradeOffer: "tradeOffer",
    /** Client → server: lock / unlock my side ({ locked }). */
    TradeLock: "tradeLock",
    /** Client → server: confirm the trade once both sides are locked (no body). */
    TradeConfirm: "tradeConfirm",
    /** Client → server: cancel the open trade (no body). */
    TradeCancel: "tradeCancel",
    /** Server → both: the live trade state (per recipient's perspective). */
    TradeUpdate: "tradeUpdate",
    /** Server → both: the trade ended ({ reason }). */
    TradeClosed: "tradeClosed",
    /** Client → server: say something in chat ({ text }). */
    Chat: "chat",
    /** Server → clients: a player said something — render a bubble ({ sessionId, text }). */
    ChatSay: "chatSay",
    /** Client → server: Ghost (admin) broadcasts a server notification ({ text }). Ignored for anyone else. */
    ServerAnnounce: "serverAnnounce",
    /** Server → all clients: show the pixel server-notification popup ({ text }). */
    ServerNotice: "serverNotice",
    /** Client → server: swing your primary weapon at the nearest enemy in range (no payload). */
    Attack: "attack",
    /** Client → server: raise your shield block stance (no payload; gated by cooldown server-side). */
    Block: "block",
    /** Client → server: I jumped (Space) — server bumps my jumpSeq so remotes play the hop (no payload). */
    Jump: "jump",
    /** Server → all: an attack landed — drives hit flash + floating damage number. */
    CombatHit: "combatHit",
    /** Server → the victim: you were downed ({ respawnInMs }). */
    CombatDied: "combatDied",
    /** Server → the victim: you've respawned at the spawn point. */
    CombatRespawn: "combatRespawn",
    /** Client → server: I spent AGILITY_STAMINA_COST stamina N times ({ ticks }) — grant N agility xp. */
    StaminaSpent: "staminaSpent",
    // ── Weed growing (Kapon wilds) ────────────────────────────────────────────────────────────
    /** Client → server: water a nearby weed plant from a carried pot ({ plantId }). */
    WaterPlant: "waterPlant",
    /** Client → server: harvest a nearby READY weed plant for buds ({ plantId }). */
    HarvestPlant: "harvestPlant",
    /** Client → server: clear a nearby ROTTEN weed plant off the ground ({ plantId }). */
    ClearPlant: "clearPlant",
    /** Client → server: refill a carried watering pot; player must stand at the water's edge
     *  ({ slotIndex? } — a specific bagged pot, else the emptiest one). */
    FillPot: "fillPot",
    /** Client → server: a completed 5s rolling channel ({ slotIndex } — the Rolling Paper's slot):
     *  consumes 1 paper + WEED_BUDS_PER_BLUNT buds, grants 1 Blunt. */
    RollBlunt: "rollBlunt",
    /** Server → joining client: the server's wall-clock epoch, to anchor the shared day/night cycle. */
    DayTime: "dayTime",
};
//# sourceMappingURL=messages.js.map